/**
 * # SDK request errors
 *
 * Every required request the SDK sends throws a subclass of
 * `SdkRequestError` when the server refuses it or does not answer, so one
 * `e instanceof SdkRequestError` covers every boundary — including
 * `@tento/lona/sheets`'s `RowsHttpBackendRequestError` (boundary `"rows-http"`). The base
 * class lives in `@tento/lona/sheets` and is re-exported here unchanged.
 *
 * `outcome` says what became of the request, the fact a caller needs before
 * retrying: `"refused"` (the server answered non-2xx; `status` is set),
 * `"unanswered"` (sent, no verdict — it may have been stored),
 * `"malformed"` (answered 2xx with a body that did not decode — it may have
 * been stored too) or `"unaddressable"` (never sent: no writable source, an
 * unresolvable recurring master). `boundary` is an `SdkRequestBoundary`, narrowed per
 * subclass like `step`.
 *
 * Failures that are not a request are plain `Error`s:
 * `PromoteIncompleteError` (every write accepted, the tree still not saved),
 * `NotPromotableError` (no promoter) and `MissingEventsLookupKeyError` (a
 * calendar with no events row to write to).
 *
 * The rows package (`@tento/lona/sheets`) reports its own requests as
 * `RowsHttpBackendRequestError` (boundary `"rows-http"`), under the same
 * lexicon; its steps are `RowsHttpBackend.Step`.
 *
 * ## Step lexicon
 *
 * `step` names the operation, one name per request: the same request carries
 * the same (boundary, step) whichever class reports it. A step is a verb: a
 * bare verb is an operation on the request's own resource (the task, event,
 * row or sheet the path names); a kebab verb-object is an operation on a
 * sub-resource (a row's cells or children, a sheet's access list, an event's
 * occurrence). No read surfaces under a `*WriteError`, with one exception:
 * the read a write gesture cannot finish without — a copy-on-write promote's
 * read-back of a sheet id its `create` found taken — is step `get` under
 * `PromoteWriteError`, carrying that GET's own method, path and status.
 *
 * `add` stays apart from `create`: `add` puts a row INTO a sheet (a new row
 * placed under a parent at a position), `create` makes a free-standing
 * resource (a task, an event, a sheet).
 *
 * | step | request | thrown as |
 * | --- | --- | --- |
 * | `create` | `POST /api/tasks` | `TaskWriteError` |
 * | `update` | `PATCH /api/tasks/{taskId}` | `TaskWriteError` |
 * | `delete` | `DELETE /api/tasks/{taskId}` | `TaskWriteError` |
 * | `create` `update` `delete` `move-to` `copy-to` | `POST /api/rows/{rowKey}/cells` (an event cell; `move-to` / `copy-to` are `moveTo` / `copyTo`) | `EventWriteError` |
 * | `move-occurrence` `update-occurrence` `delete-occurrence` | the `POST /api/rows/{rowKey}/cells` writes of one recurring-occurrence gesture (`EventsClient.moveOccurrence` / `updateOccurrence` / `deleteOccurrence`); status and reason are the failed write's | `EventWriteError` |
 * | `list-tz-transitions` | `GET /api/user/tz/transitions` (`client.events.listTzTransitions`) | `SdkRequestError` (boundary `events`) |
 * | `add` | `POST /api/sheets/{sheetKey}/row`; `POST /api/rows` (rows `RowsHttpBackend`; `SchedulingLinksClient.create`) | `RowWriteError`, `PromoteWriteError`, `RowsHttpBackendRequestError` |
 * | `update` | `PATCH /api/rows/{rowKey}` (`row.update`; `row.updateDurable(...).settled`) | `RowWriteError`, `PromoteWriteError`, `RowsHttpBackendRequestError` |
 * | `get` | `GET /api/rows/{rowKey}` | `SdkRequestError` (boundary `rows`; boundary `events` when the primary events row cannot serve, outcome `unaddressable`) |
 * | `check-exists` | `GET /api/rows/{rowKey}/exists` | `SdkRequestError` (boundary `rows`) |
 * | `list-linkable` | `GET /api/rows/linkable` | `SdkRequestError` (boundary `rows`) |
 * | `delete` | `DELETE /api/sheets/{sheetKey}/row/{rowKey}`; `DELETE /api/rows/{rowKey}` (rows `RowsHttpBackend`) | `RowWriteError`, `RowsHttpBackendRequestError` |
 * | `reorder` | `POST /api/sheets/{sheetKey}/row/{rowKey}/reorder` | `RowWriteError`, `PromoteWriteError` |
 * | `restore` | `POST /api/rows/{rowKey}/restore` | `RowWriteError` |
 * | `link` | `POST /api/rows/linkable` | `RowWriteError` |
 * | `hydrate-children` | `POST /api/sheets/{sheetKey}/row/{rowKey}/hydrate-children` | `RowWriteError`, `PromoteWriteError` |
 * | `get-cells` | `GET /api/rows/{rowKey}/cells` | `SdkRequestError` (boundary `cells`; boundary `events` from `events.ensureLoaded` when the primary events row is not indexed, outcome `unaddressable`) |
 * | `get-sheet-cells` | `GET /api/sheets/{sheetKey}/cells` (the sheets backend's sheet-wide range read, `Backend.fetchSheetCells`) | `SdkRequestError` (boundary `cells`) |
 * | `get-cells-batch` | `POST /api/sheets/{sheetKey}/cells/batch` answered 429 (reason `rate_limited`); unanswered (its transport rejected, `RowsHttpBackend.fetchCellsBatch`) | `BatchFetchThrottledError` (boundary `rows-http`); `SdkRequestError` (boundary `cells`, outcome `unanswered`) |
 * | `write-cell` | `POST /api/rows/{rowKey}/cells` (a date-keyed cell; a held id-keyed one carries its `id`) | `CellWriteError` |
 * | `clear-cell` | `POST /api/rows/{rowKey}/cells` (a date-keyed cell, `data: null`) | `CellWriteError` |
 * | `write-timepoint-cell` | `POST /api/rows/{rowKey}/cells` (a timepoint cell: `date` + `endDate` datetimes) | `CellWriteError`, `RowsHttpBackendRequestError` |
 * | `clear-timepoint-cell` | `POST /api/rows/{rowKey}/cells` (`id` + `data: null`: a held timepoint cell deleted by its id) | `CellWriteError`, `RowsHttpBackendRequestError` |
 * | `move-cell` | none: `moveCell`'s refusal of a target it cannot move (outcome `unaddressable`, nothing sent; a move posts `write-timepoint-cell`) | `CellWriteError` |
 * | `replace-cell` | none: `replaceCell`'s refusal of a target it cannot replace (outcome `unaddressable`, nothing sent; a replace posts `write-timepoint-cell`) | `CellWriteError` |
 * | `generate-cell` | `POST /api/rows/{rowKey}/cells/generate` | `CellWriteError` |
 * | `create` | `POST /api/sheets` | `SheetWriteError`, `PromoteWriteError` |
 * | `get` | `GET /api/sheets/{sheetKey}` | `SheetReadError`, `SheetOpenError`, `RowsHttpBackendRequestError`; `PromoteWriteError` (a promote's read-back of a taken id, answered 401/403: reason `cross_owner`; 404 or empty: reason `not_found`) |
 * | `list` | `GET /api/sheets/list` | `SheetReadError` |
 * | `update` | `PATCH /api/sheets/{sheetKey}` (`sheets.updateDurable(...).settled`) | `SheetWriteError` |
 * | `delete` | `DELETE /api/sheets/{sheetKey}` | `SheetWriteError` |
 * | `reset` | `DELETE /api/sheets/{sheetKey}` (a forked built-in) | `SheetWriteError` |
 * | `restore` | `POST /api/sheets/{sheetKey}/restore` | `SheetWriteError` |
 * | `reorder` | `POST /api/sheets/{sheetKey}/reorder` | `SheetWriteError` |
 * | `get-acl` | `GET /api/sheets/{sheetKey}/acl` | `SheetReadError` |
 * | `grant-acl` | `POST /api/sheets/{sheetKey}/acl` | `SheetWriteError` |
 * | `revoke-acl` | `DELETE /api/sheets/{sheetKey}/acl` | `SheetWriteError` |
 * | `poll-status` | `GET /api/sync/{syncKey}/status` answered 429 (reason `rate_limited`) | `SyncPollThrottledError` (boundary `sync`) |
 *
 * Boundaries with no dedicated subclass throw the base class through
 * `SdkRequestError.checkRequest(boundary, step, …)` under the same rule:
 * `calendars` `list` (`GET /api/calendars`), `user` `get` /
 * `list-auths` / `get-customer-id` / `list-product-keys` / `update`
 * (`PATCH /api/user/themes`: `themes.setDurable(...).settled`), `sync`
 * `reset` / `trigger-row-sync`, `scheduling-links` `list` / `get`
 * (`GET /api/rows/{rowKey}`: `getBookingLink`) / `get-cells`
 * (`GET /api/rows/{rowKey}/cells`: `getBooking`).
 *
 * One step sits outside the per-request rule: a TOLERANT request (one that
 * reads empty on failure) reports its failure to the client's
 * `RequestErrorHandler` as boundary `"request"`, step `"tolerate-failure"`,
 * whatever the request was; `method` and `path` name it.
 */
import type { Option, RowNetworkKey, SheetId } from "@tento-lona/sheets";
import type { Result } from "@tento-core/result";
import { Tracing } from "@tento-core/tracing";
import type { ResponseError } from "@tento-core/typed-api";
import {
  RowWriteError,
  type SdkRequestBoundary,
  SdkRequestError,
  type SdkRequestFailure,
  type SdkRequestMethod,
  type SdkRequestOutcome,
  type SdkRequestReason,
  SheetNetworkKey,
} from "@tento-lona/sheets";

// `RowWriteError` lives with `SdkRequestError` in `@tento/lona/sheets` (the
// rows package's `Sheet.setRendererDisplayAttr` throws it too) and is
// re-exported here unchanged.
export {
  RowWriteError,
  type SdkRequestBoundary,
  SdkRequestError,
  type SdkRequestFailure,
  type SdkRequestMethod,
  type SdkRequestOutcome,
  type SdkRequestReason,
};

/**
 * Thrown by `TasksClient.create` / `update` / `delete` when the task
 * endpoint (`POST /api/tasks`, `PATCH|DELETE /api/tasks/{taskId}`) rejects
 * the write or does not answer. `update` has already rolled its optimistic
 * move back; `create` and `delete` applied nothing locally.
 *
 * Also thrown (`outcome: "unaddressable"`, `method` of the intended write)
 * when a mutation cannot be addressed at all, e.g. the task row is missing.
 */
export class TaskWriteError extends SdkRequestError {
  declare readonly boundary: "tasks";
  declare readonly step: TaskWriteError.Step;

  constructor(init: SdkRequestError.SubclassInit<TaskWriteError.Step>) {
    super({ ...init, boundary: "tasks" });
    this.name = "TaskWriteError";
  }

  /** Send a required task write; throw `TaskWriteError` on failure. */
  static check<T>(
    step: TaskWriteError.Step,
    method: SdkRequestMethod,
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new TaskWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
}

/** Types that go with `TaskWriteError`: `Step`. */
export namespace TaskWriteError {
  export type Step = "create" | "update" | "delete";
}

/**
 * Thrown by `EventsClient.create` / `update` / `delete` / `moveTo` /
 * `copyTo` / `moveOccurrence` / `updateOccurrence` / `deleteOccurrence`
 * (and the row's `EventOps` beneath them) when the event write
 * fails — the cells write (`POST /api/rows/{rowKey}/cells`) was rejected or
 * unanswered, or the event has no writable source row. The optimistic
 * placeholder (create), move (update / moveTo), removal (delete), or copy
 * has already been dropped. `cause` carries the underlying error when one
 * was wrapped. Also the rejection of `EventsClient.updateDurable(...).settled`.
 *
 * `method` and `path` name the request that failed: `POST
 * /api/rows/{rowKey}/cells` only when the cells write itself failed. When
 * the event's source row could not be opened the write was never sent — the
 * error is outcome `"unaddressable"` naming that open (`GET
 * /api/rows/{rowKey}`), the open's own error its `cause`. An error that is
 * not a request's failure (`LonaSdk.Client.DisposedError`, a bug) is
 * rethrown as is, never wrapped.
 *
 * `outcome` tells a retry apart from a duplicate: `"refused"` (the server
 * said no), `"unanswered"` (no verdict — the write may have persisted; the
 * events client has re-read the row), `"malformed"` (an answer that did not
 * decode — it may have persisted too) or `"unaddressable"` (never sent — no
 * writable source row, a source row that did not open, an unresolvable
 * recurring master).
 */
export class EventWriteError extends SdkRequestError {
  declare readonly boundary: "events";
  declare readonly step: EventWriteError.Step;

  constructor(init: SdkRequestError.SubclassInit<EventWriteError.Step>) {
    super({ ...init, boundary: "events" });
    this.name = "EventWriteError";
  }

  /** Send a required event write; throw `EventWriteError` on failure. */
  static check<T>(
    step: EventWriteError.Step,
    method: SdkRequestMethod,
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new EventWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }

  /**
   * Re-report a request's failure from below the events boundary (the row's
   * `EventOps`, a source-row open, one request of an occurrence gesture) as
   * an `EventWriteError` of `step`, keeping it as `cause`. The request is
   * the one that failed: its method, path, outcome, status, reason and
   * details are kept, so a refused `GET` of the source row never reads as a
   * refused cells `POST`. An `EventWriteError` of the same step passes
   * through. Only a request's failure is wrapped — an error that is not one
   * (a disposed client, a bug) is rethrown as is by the events verbs, never
   * labelled with an outcome it did not have.
   */
  static from(step: EventWriteError.Step, err: SdkRequestError): EventWriteError {
    if (err instanceof EventWriteError && err.step === step) return err;
    const wrapped = new EventWriteError({
      step,
      method: err.method,
      path: err.path,
      failure: err.failure,
      ...(err.reason != null ? { reason: err.reason } : {}),
      details: err.details,
    });
    wrapped.cause = err;
    return wrapped;
  }
}

/** Types that go with `EventWriteError`: `Step`. */
export namespace EventWriteError {
  export type Step =
    | "create"
    | "update"
    | "delete"
    | "move-to"
    | "copy-to"
    | "move-occurrence"
    | "update-occurrence"
    | "delete-occurrence";
}

/**
 * Thrown by the cell writes of both row facades (their writable facades'
 * `write`, `moveCell`, `clear`, addressed by a `CellTarget`) when the cell write
 * (`POST /api/rows/{rowKey}/cells`) is rejected or unanswered, and by
 * `generateCell` (step `"generate-cell"`,
 * `POST /api/rows/{rowKey}/cells/generate`). The local mirror is applied
 * only after the write succeeds, so nothing local needs undoing — except
 * `move`, which moves optimistically and has put the cell back before this
 * is thrown. Also the rejection of a durable cell write's `settled`
 * (the writable facades' `writeDurable` / `clearDurable`), date-keyed or id-keyed, and —
 * outcome `"unaddressable"`, never sent — the verbs' refusal of a target
 * they cannot address: `writeDurable` / `clearDurable` of a timepoint
 * target (as its `settled` rejection: a durable verb never throws), `clear`
 * of a `range`, `move` / `replace` of a date-keyed cell or of a cell id the
 * row does not hold (step `"move-cell"` / `"replace-cell"`).
 */
export class CellWriteError extends SdkRequestError {
  declare readonly boundary: "cells";
  declare readonly step: CellWriteError.Step;

  constructor(init: SdkRequestError.SubclassInit<CellWriteError.Step>) {
    super({ ...init, boundary: "cells" });
    this.name = "CellWriteError";
  }

  /** Send a required cell write; throw `CellWriteError` on failure. */
  static check<T>(
    step: CellWriteError.Step,
    method: SdkRequestMethod,
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new CellWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
}

/** Types that go with `CellWriteError`: `Step`. */
export namespace CellWriteError {
  /**
   * The cell verbs' steps, named like the verbs (`write`, `clear`, `move`,
   * `generate`): `write-cell` / `clear-cell` post a date-keyed cell (a held
   * id-keyed cell carries its id), `write-timepoint-cell` posts a timepoint
   * (sub-day, datetime-ranged) cell, `clear-timepoint-cell` deletes a held
   * timepoint cell by its id, `move-cell` / `replace-cell` are `move`'s /
   * `replace`'s refusal of a target they cannot move (the move itself posts
   * `write-timepoint-cell`), `generate-cell` is `generateCell`.
   */
  export type Step =
    | "write-cell"
    | "clear-cell"
    | "write-timepoint-cell"
    | "clear-timepoint-cell"
    | "move-cell"
    | "replace-cell"
    | "generate-cell";
}

/**
 * Thrown by `SheetsClient` (`client.sheets`) when a sheet WRITE
 * (`/api/sheets…`) is rejected or unanswered: `create`, `delete`, `reorder`,
 * the checked `update`,
 * the durable `updateDurable` (step `"update"`, as its `settled` rejection),
 * the undo `restore`, `resetBuiltinFork` (step `"reset"`), `grantAcl`
 * (step `"grant-acl"`) and `revokeAcl` (step `"revoke-acl"`). The metadata
 * writes paint first: `update` / `updateDurable` apply the patch to the
 * cached list at once and take it back on a refusal (the entry returns to
 * its confirmed state with every other write in flight replayed in issue
 * order). The others mirror after: nothing local changes until the server
 * accepted them, so a failure leaves nothing to undo. The reads throw
 * `SheetReadError`.
 *
 * `status` carries the numeric HTTP status, which the copy-on-write promote
 * reads to report `PromoteWriteError.status`.
 */
export class SheetWriteError extends SdkRequestError {
  declare readonly boundary: "sheets";
  declare readonly step: SheetWriteError.Step;

  constructor(init: SdkRequestError.SubclassInit<SheetWriteError.Step>) {
    super({ ...init, boundary: "sheets" });
    this.name = "SheetWriteError";
  }

  /** Send a required sheet write; throw `SheetWriteError` on failure. */
  static check<T>(
    step: SheetWriteError.Step,
    method: SdkRequestMethod,
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new SheetWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
}

/** Types that go with `SheetWriteError`: `Step`. */
export namespace SheetWriteError {
  export type Step =
    | "create"
    | "update"
    | "delete"
    | "reorder"
    | "restore"
    | "reset"
    | "grant-acl"
    | "revoke-acl";
}

/**
 * Thrown by `SheetsClient`'s reads when the sheet request is rejected or
 * unanswered: `list` / `listDeleted` (step `"list"`), `get` (step `"get"`; a
 * 404 there resolves `null` instead) and `getAcl` (step `"get-acl"`). A read
 * changes nothing locally, so there is nothing to undo.
 */
export class SheetReadError<F extends SdkRequestFailure = SdkRequestFailure> extends SdkRequestError<F> {
  declare readonly boundary: "sheets";
  declare readonly step: SheetReadError.Step;
  declare readonly method: "GET";

  constructor(init: SdkRequestError.SubclassInit<SheetReadError.Step, "GET", F>) {
    super({ ...init, boundary: "sheets" });
    this.name = "SheetReadError";
  }

  /** Send a required sheet read; throw `SheetReadError` on failure. */
  static check<T>(
    step: SheetReadError.Step,
    method: "GET",
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new SheetReadError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
}

/** Types that go with `SheetReadError`: `Step`. */
export namespace SheetReadError {
  export type Step = "list" | "get" | "get-acl";
}

/**
 * Thrown by `LonaSdk.Client.open(key)` when the sheet read
 * (`GET /api/sheets/{sheetKey}`, step `"get"`) is rejected or unanswered — a
 * 401 / 403 / 5xx, or a transport failure (`status: null`). It is never read
 * as "no such sheet": only an explicit miss is (see `SheetNotFoundError`), so
 * an expired session or a server outage can no longer open a fresh seeded
 * ephemeral in place of the user's saved sheet.
 *
 * A `SheetReadError`, so one `instanceof SheetReadError` covers every failed
 * sheet read. `path` is the concrete request path; `sheetKey` is the key as
 * passed.
 */
export class SheetOpenError extends SheetReadError<SdkRequestError.Answered> {
  declare readonly step: "get";
  /** The key `open` was called with (id, `ss_<hex>`, or lookup key). */
  readonly sheetKey: SheetNetworkKey;

  constructor(init: SheetOpenError.Init) {
    super({
      step: "get",
      method: "GET",
      path: `/api/sheets/${init.sheetKey}`,
      failure: init.failure,
      ...(init.reason !== undefined ? { reason: init.reason } : {}),
      ...(init.details !== undefined ? { details: init.details } : {}),
    });
    this.name = "SheetOpenError";
    this.sheetKey = init.sheetKey;
  }
}

/** Types that go with `SheetOpenError`: `Init`. */
export namespace SheetOpenError {
  /** What the constructor takes: the key, and a refusal or no answer. */
  export type Init = {
    readonly sheetKey: SheetNetworkKey;
    readonly failure: SdkRequestError.Answered;
    readonly reason?: SdkRequestReason;
    readonly details?: string;
  };
}

/**
 * The sheet read ANSWERED that there is no such sheet — HTTP 404, or HTTP 200
 * with a `null` body (the route answers `Option<UserSheetWithRows>`) — and the
 * key is not a builtin catalog key (a catalog key opens its seeded ephemeral
 * instead). A `SheetOpenError`, so one `instanceof SheetOpenError` covers
 * every failed open; `status` is the status the server answered (404 or 200),
 * `reason` is `"not_found"`.
 */
export class SheetNotFoundError extends SheetOpenError {
  /** `status` is what the server answered: 404, or 200 with a `null` body. */
  constructor(init: { readonly sheetKey: SheetNetworkKey; readonly status: number }) {
    super({
      sheetKey: init.sheetKey,
      failure: { outcome: "refused", status: init.status },
      reason: "not_found",
      details: `sheet not found: ${init.sheetKey}`,
    });
    this.name = "SheetNotFoundError";
  }
}

/**
 * A required copy-on-write promote write the server did not accept: a non-2xx
 * answer or no answer at all. Every promote write — the sheet `create`, a
 * root `add`, `hydrate-children`, the corrective `update`
 * (`PATCH /api/rows/{row}`), the re-parenting `reorder` — is CHECKED, because
 * the plain `TypedApi.post` / `.patch`
 * hand a non-2xx to the client's request-error handler
 * (`ClientConfig.requestErrorHandler`) and RESOLVE with `undefined`: awaiting
 * them proves nothing, and the promote used to record the write as done and
 * mark the handle synced over a half-written tree. Thrown out of the
 * promoter, this leaves the handle `"local"` with its promoter installed, so
 * the next `ensurePromoted()` retries and resumes. The message names the
 * step, the request and the server's `{ reason, details }`.
 *
 * Also thrown (step `"get"`, `GET /api/sheets/{sheetKey}`, method `"GET"`)
 * when the `create` found the sheet id taken and the promote's read-back of
 * it is refused: 401/403 — the id belongs to another owner (reason
 * `"cross_owner"`) — or 404 / an empty answer (reason `"not_found"`).
 * `status` is that read's. The SDK shows no UI for it; the caller reports it.
 *
 * An `SdkRequestError` (boundary `"promote"`), so one
 * `instanceof SdkRequestError` covers a promote failure beside every other
 * checked SDK request; `path` is the concrete request path (the server row id
 * filled in), and `status` is `null` unless the server refused it.
 */
export class PromoteWriteError extends SdkRequestError {
  declare readonly boundary: "promote";
  declare readonly step: PromoteWriteError.Step;
  declare readonly method: "POST" | "PATCH" | "GET";

  constructor(init: SdkRequestError.SubclassInit<PromoteWriteError.Step, "POST" | "PATCH" | "GET">) {
    super({ ...init, boundary: "promote" });
    this.name = "PromoteWriteError";
  }

  /** Send a required promote write; throw `PromoteWriteError` on failure. */
  static check<T>(
    step: PromoteWriteError.Step,
    method: "POST" | "PATCH",
    path: string,
    send: () => Promise<Result<T, ResponseError>>,
  ): Promise<T> {
    return SdkRequestError.sendChecked(send, (answer) =>
      new PromoteWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
}

/** Types that go with `PromoteWriteError`: `Step`. */
export namespace PromoteWriteError {
  /**
   * The promote request that failed (see the step lexicon above): a write,
   * or `"get"`, the read-back of a sheet id the `create` found taken.
   */
  export type Step =
    /** `POST /api/sheets`: creating the sheet the promote copies into. */
    | "create"
    /** `GET /api/sheets/{sheetKey}`: the read-back of a sheet id `create` found taken. */
    | "get"
    /** `POST /api/sheets/{sheetKey}/row`: adding one row to the new sheet. */
    | "add"
    /** `POST /api/sheets/{sheetKey}/row/{rowKey}/hydrate-children`: a row's nested children. */
    | "hydrate-children"
    /** `PATCH /api/rows/{rowKey}`: a row field the hydrate route does not keep. */
    | "update"
    /** `POST /api/sheets/{sheetKey}/row/{rowKey}/reorder`: a row's position. */
    | "reorder";
}

/**
 * A copy-on-write promote whose writes were all accepted, yet the server
 * sheet still does not hold every local row it must save — so the handle is
 * NOT marked synced: it stays `"local"` with its promoter installed, and the
 * next `ensurePromoted()` resumes from what is stored.
 *
 * The promote re-creates what is missing through the real routes (a root the
 * interrupted `create_with_rows` never added: `POST /api/sheets/{sheet}/row`;
 * a nested row: `hydrate-children`, or add + `reorder` under a parent the
 * server already hydrated), so this is left for rows it cannot establish a
 * one-to-one server row for — e.g. two local rows naming the same `lookupKey`,
 * which the server resolves to ONE reused row.
 *
 * Policy for rows the server lacks: the LOCAL tree is what is being saved, so
 * a row another session deleted is re-created, never adopted as "gone"; a
 * server row the local tree does not hold is left untouched. A promote never
 * reports synced over a local row with no saved counterpart.
 *
 * "Saved" means the tree EDGE and the metadata, not only an id: a row whose
 * server row sits under the wrong parent or at the wrong position, or still
 * lacks the `timeScale` / `heightPx` the promote corrects, is not saved
 * either (`misplacedRowKeys`). A shared lookup-keyed ROOT is exempt — the
 * promote never moves or patches the user's canonical row.
 *
 * Not a refused request, so a plain `Error`, not an `SdkRequestError`.
 */
export class PromoteIncompleteError extends Error {
  /** The sheet being promoted. */
  readonly sheetId: SheetId;
  /**
   * Local row keys (rows-side raw ids, in their wire spelling) with no
   * correctly saved server row: no server row at all, or one listed in
   * `misplacedRowKeys`.
   */
  readonly missingRowKeys: readonly RowNetworkKey[];
  /** How many local rows the promote had to save. */
  readonly localRows: number;
  /**
   * The subset of `missingRowKeys` that HAS a server row, but under the wrong
   * parent, at the wrong position, or without its corrected metadata.
   */
  readonly misplacedRowKeys: readonly RowNetworkKey[];

  constructor(
    sheetId: SheetId,
    missingRowKeys: readonly RowNetworkKey[],
    localRows: number,
    misplacedRowKeys: readonly RowNetworkKey[],
  ) {
    super(
      `cow-promote incomplete for ${sheetId}: ${missingRowKeys.length} of ` +
        `${localRows} local rows have no saved server row ` +
        `(${missingRowKeys.join(", ")})` +
        (misplacedRowKeys.length > 0
          ? `, ${misplacedRowKeys.length} of them stored out of place ` +
            `(${misplacedRowKeys.join(", ")})`
          : "") +
        `; the sheet stays local`,
    );
    this.name = "PromoteIncompleteError";
    this.sheetId = sheetId;
    this.missingRowKeys = missingRowKeys;
    this.localRows = localRows;
    this.misplacedRowKeys = misplacedRowKeys;
    Tracing.log("cow-promote-incomplete", {
      sheetId: sheetId.toString(),
      missing: missingRowKeys.length,
      misplaced: misplacedRowKeys.length,
      localRows,
      missingRowKeys: missingRowKeys.join(","),
    });
  }
}

/**
 * `SheetHandle.ensurePromoted()` on a `"local"` handle that has no promoter:
 * it can never be saved (`LonaSdk.Client.parseSheet` parsed it with
 * `mode: "ephemeral"` or `mode: "template"` rather than
 * `LonaSdk.Client.openEphemeral` opening it), so `ensurePromoted()`
 * REJECTS rather than resolving as if a save had happened. Not a refused
 * request, so a plain `Error`.
 */
export class NotPromotableError extends Error {
  readonly sheetId: SheetId;
  readonly persistence: "local" | "synced";

  constructor(sheetId: SheetId, persistence: "local" | "synced") {
    super(
      persistence === "local"
        ? `This sheet cannot be promoted. ${sheetId} is local and has no ` +
          `promoter, so it cannot be saved; open it with ` +
          `\`openEphemeral(spec, …)\` to get a promotable handle.`
        : `This sheet cannot be promoted. ${sheetId} is already saved.`,
    );
    this.name = "NotPromotableError";
    this.sheetId = sheetId;
    this.persistence = persistence;
  }
}
